import React, { useState } from "react";
import { Form, Formik } from "formik";
import moment from "moment";
import InputField from "../../forms/input-field";
import ImageField from "../../forms/images-field";
import TextAreaField from "../../forms/textarea-field";
import TabsComponent from "../../helper-components/tabs-component";
import ApproveDeclineActions from "../approve-decline-actions";
import { toImageUrl } from "../../../utils/image-url";
import { url } from "../../../redux/config";

// The declaration's `status` stays PENDING after a decision, so the
// approve/reject flags decide what is shown.
export const declarationStatusLabel = (item) => {
  if (item?.is_approved) return "Approved";
  if (item?.is_rejected) return "Declined";
  if (!item?.status) return "--";
  return item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase();
};

// Files arrive as absolute URLs, /api/... paths or bare upload paths; ImageField
// uses the value as-is for src and href, so each is made absolute here.
const toFileUrl = (value) => {
  if (!value) return "";
  const path = String(value);
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/api/")) return `${url.replace(/\/api$/, "")}${path}`;
  return toImageUrl(path.replace(/^\/+/, ""));
};

const toFileList = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .filter(Boolean)
    .map(toFileUrl);

// Long labels ("Declaration & Compliance Agreement") wrap inside the label
// column; the fields' default labels never wrap and run into the input.
const inputLabelClass =
  "max-w-[10.5rem] whitespace-normal pr-2 text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]";
const imageLabelClass =
  "flex items-start mt-3 [&>span]:max-w-[10.5rem] [&>span]:pr-2";

const withMobilePrefix = (value) => (value ? `+91 ${value}` : "");

const formatDate = (raw) => (raw ? moment(raw).format("DD/MM/YYYY") : "--");

const headClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]";

const cellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap";

const historyHeadings = [
  "#",
  "UPDATED ON",
  "FRANCHISEE NAME",
  "FIRM NAME",
  "FIRM TYPE",
  "DESIGNATION",
  "PLACE",
  "STATUS",
  "REASON",
];

const emptyMessageClass =
  "px-5 xl:px-10 mt-5 text-[length:--list-table-row-font-size] text-[var(--list-table-row-fg-color)]";

const SelfDeclarationView = ({
  declaration = {},
  permissionFn = () => {},
}) => {
  const [currentTab, setCurrentTab] = useState("Partner Details");

  const partners = Array.isArray(declaration?.partner_details)
    ? declaration.partner_details
    : [];
  const employees = Array.isArray(declaration?.relationship_declaration)
    ? declaration.relationship_declaration
    : [];
  const history = Array.isArray(declaration?.history_details)
    ? declaration.history_details
    : [];

  const signatures = toFileList(declaration?.signature);

  const initialValues = {
    franchisee_name: declaration?.franchisee_name ?? "",
    firm_type: declaration?.firm_type ?? "",
    designation: declaration?.designation ?? "",
    mobile_number: withMobilePrefix(declaration?.mobile_number),
    place: declaration?.place ?? "",
    status: declarationStatusLabel(declaration),
    reason: declaration?.reason ?? "",
    // Decision state, as the /store/approve and /store/reject endpoints name it.
    is_approved: !!declaration?.is_approved,
    is_rejected: !!declaration?.is_rejected,
    signatures: signatures.length ? signatures : [""],
    store_dc_document: toFileUrl(declaration?.store_dc_document),
    partners: partners.map((partner) => ({
      partner_name: partner?.partner_name ?? "",
      signatures: toFileList(partner?.signature),
    })),
    employees: employees.map((employee) => ({
      employee_name: employee?.employee_name ?? "",
      relationship: employee?.relationship ?? "",
      designation: employee?.designation ?? "",
      department: employee?.department ?? "",
      mobile_number: withMobilePrefix(employee?.mobile_number),
    })),
  };

  return (
    <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={() => {}}>
      {({ values, setFieldValue }) => (
        <Form className="pt-5 pb-10">
          <div className="flex flex-wrap">
            <div className="w-full 2xl:w-[35%] lg:w-[50%] px-5 xl:px-10">
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="franchisee_name"
                label="Name of Franchisee / Franchisee Owner"
                labelWidth="10.5rem"
              />
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="firm_type"
                label="Firm Type"
                labelWidth="10.5rem"
              />
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="designation"
                label="Designation"
                labelWidth="10.5rem"
              />
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="mobile_number"
                label="Mobile Number"
                labelWidth="10.5rem"
              />
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="place"
                label="Place"
                labelWidth="10.5rem"
              />
            </div>
            <div className="w-full 2xl:w-[35%] lg:w-[50%] px-5 xl:px-10">
              <InputField
                labelClass={inputLabelClass}
                placeholder="--"
                isDisabled={true}
                type="text"
                name="status"
                label="Status"
                labelWidth="10.5rem"
              />
              <TextAreaField
                isDisabled={true}
                name="reason"
                label="Remarks"
                labelWidth="10.5rem"
              />
              {values.signatures.map((_, index) => (
                <ImageField
                  key={index}
                  labelClass={imageLabelClass}
                  isDisabled={true}
                  mainLabel={index === 0 ? "Digital Signature" : ""}
                  label="No signature"
                  labelWidth="10.5rem"
                  name={`signatures[${index}]`}
                />
              ))}
            </div>
          </div>
          {values.store_dc_document && (
            <div className="px-5 xl:px-10 mt-4 mb-6">
              <p className="text-sm font-medium text-gray-600 mb-2">
                Declaration & Compliance Agreement
              </p>
              <div style={{ width: "100%", height: "800px" }}>
                <embed
                  src={values.store_dc_document}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                />
              </div>
            </div>
          )}

          <TabsComponent
            handleClickfn={(item) => {
              setCurrentTab(item);
            }}
            itemTabs={itemTabs}
            setCurrentTab={setCurrentTab}
            currentTab={currentTab}
          />

          {currentTab === "Partner Details" &&
            (values.partners.length ? (
              <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10 mt-5">
                {values.partners.map((partner, index) => (
                  <div
                    key={index}
                    className="mb-6 border border-D0D5DD rounded-lg p-4"
                  >
                    <span className="text-sm font-semibold text-gray-600">
                      Partner {index + 1}
                    </span>
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`partners[${index}].partner_name`}
                      label="Partner Name"
                      labelWidth="10.5rem"
                    />
                    {(partner.signatures.length ? partner.signatures : [""]).map(
                      (_, signIndex) => (
                        <ImageField
                          key={signIndex}
                          labelClass={imageLabelClass}
                          isDisabled={true}
                          mainLabel={signIndex === 0 ? "Digital Signature" : ""}
                          label="No signature"
                          labelWidth="10.5rem"
                          name={`partners[${index}].signatures[${signIndex}]`}
                        />
                      )
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className={emptyMessageClass}>No partner details.</p>
            ))}

          {currentTab === "Employee Details" &&
            (values.employees.length ? (
              <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10 mt-5">
                {values.employees.map((_, index) => (
                  <div
                    key={index}
                    className="mb-6 border border-D0D5DD rounded-lg p-4"
                  >
                    <span className="text-sm font-semibold text-gray-600">
                      Employee {index + 1}
                    </span>
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`employees[${index}].employee_name`}
                      label="Employee Name"
                      labelWidth="10.5rem"
                    />
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`employees[${index}].relationship`}
                      label="Relationship"
                      labelWidth="10.5rem"
                    />
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`employees[${index}].designation`}
                      label="Designation"
                      labelWidth="10.5rem"
                    />
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`employees[${index}].department`}
                      label="Department"
                      labelWidth="10.5rem"
                    />
                    <InputField
                      labelClass={inputLabelClass}
                      placeholder="--"
                      isDisabled={true}
                      type="text"
                      name={`employees[${index}].mobile_number`}
                      label="Mobile No"
                      labelWidth="10.5rem"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className={emptyMessageClass}>
                No relatives declared in the company.
              </p>
            ))}

          {currentTab === "History" && (
            <div className="px-5 xl:px-10 mt-5">
              <div className="tab-container outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
                <table className="w-full border-collapse mt-0">
                  <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                    <tr className="h-[50px]">
                      {historyHeadings.map((heading) => (
                        <th key={heading} className={headClass}>
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.length ? (
                      history.map((row, index) => (
                        <tr key={row?.store_declaration_id ?? index}>
                          <td className={cellClass}>{index + 1}</td>
                          <td className={cellClass}>
                            {formatDate(row?.update_date ?? row?.created_date)}
                          </td>
                          <td className={cellClass}>{row?.franchisee_name || "--"}</td>
                          <td className={cellClass}>{row?.firm_name || "--"}</td>
                          <td className={cellClass}>{row?.firm_type || "--"}</td>
                          <td className={cellClass}>{row?.designation || "--"}</td>
                          <td className={cellClass}>{row?.place || "--"}</td>
                          <td className={cellClass}>{declarationStatusLabel(row)}</td>
                          <td className={cellClass}>{row?.reason || "--"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={historyHeadings.length}
                          className={`${cellClass} text-center`}
                        >
                          No earlier versions of this declaration.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {permissionFn("store_document_process")?.approver && (
            <div className="sticky mt-6" style={{ bottom: "0" }}>
              <div className="flex items-center justify-between px-10 bg-white py-4 invoice-bottom-container">
                <ApproveDeclineActions
                  storeId={declaration?.store_id}
                  extraPayload={{
                    store_declaration_id: declaration?.store_declaration_id,
                  }}
                  approveKey="is_approved"
                  rejectKey="is_rejected"
                  reasonKey="reason"
                  record={declaration}
                  isApproved={!!values.is_approved}
                  isRejected={!!values.is_rejected}
                  lockAfterDecline={true}
                  // Both buttons freeze on whichever decision the server
                  // accepted; a failed call leaves them live to retry.
                  onDecided={async (_, decision) => {
                    const approved = decision === "approved";
                    await setFieldValue("is_approved", approved);
                    await setFieldValue("is_rejected", !approved);
                    await setFieldValue(
                      "status",
                      approved ? "Approved" : "Declined"
                    );
                  }}
                />
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SelfDeclarationView;

const itemTabs = ["Partner Details", "Employee Details", "History"];
